/**
 * Typed, public-id-based REST API client for the Event Scoring System frontends.
 *
 * Design constraints (see design.md "ID Strategy" / "API Routes"):
 *   - Every route, request body, and response payload uses prefixed-NanoID
 *     *public IDs* exclusively. Snowflake IDs never cross the API boundary.
 *   - Protected routes carry `Authorization: Bearer <token>`; the token comes
 *     from the injected {@link ApiClientConfig.getToken} callback so the client
 *     stays decoupled from the Cognito session implementation.
 *   - `GET /public/*` routes require no token. The public leaderboard endpoint
 *     returns `403 { error: "AUTHENTICATION_REQUIRED" }` for non-public events;
 *     the client surfaces this as an {@link ApiError} with that code.
 *
 * The client wraps `fetch`, serializes JSON request bodies, parses JSON
 * responses, and maps every non-2xx response to a typed {@link ApiError}.
 */
import { ApiError, isApiErrorEnvelope } from './errors.js';
/** Joins a base URL and a path, tolerating a trailing slash on the base. */
function joinUrl(baseUrl, path) {
    const base = baseUrl.replace(/\/+$/, '');
    const suffix = path.startsWith('/') ? path : `/${path}`;
    return `${base}${suffix}`;
}
/** Appends query parameters, skipping nullish values. */
function applyQuery(url, query) {
    if (!query)
        return url;
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(query)) {
        if (value === undefined || value === null)
            continue;
        params.append(key, String(value));
    }
    const qs = params.toString();
    return qs ? `${url}?${qs}` : url;
}
/**
 * Creates a typed API client bound to a base URL and token provider.
 */
export function createApiClient(config) {
    const doFetch = config.fetch ?? ((input, init) => fetch(input, init));
    async function request(method, path, options = {}) {
        const url = applyQuery(joinUrl(config.baseUrl, path), options.query);
        const headers = { Accept: 'application/json' };
        const hasBody = options.body !== undefined;
        if (hasBody) {
            headers['Content-Type'] = 'application/json';
        }
        if (!options.public && config.getToken) {
            const token = await config.getToken();
            if (token) {
                headers['Authorization'] = `Bearer ${token}`;
            }
        }
        let response;
        try {
            response = await doFetch(url, {
                method,
                headers,
                body: hasBody ? JSON.stringify(options.body) : undefined,
                signal: options.signal,
            });
        }
        catch (cause) {
            // Transport-level failure (offline, DNS, CORS, abort). Surface as ApiError.
            throw new ApiError({
                statusCode: 0,
                code: 'NETWORK_ERROR',
                message: cause instanceof Error ? cause.message : 'Network request failed',
                body: cause,
            });
        }
        // 204 No Content (and other empty bodies) → no JSON to parse.
        if (response.status === 204) {
            if (response.ok) {
                return undefined;
            }
            throw new ApiError({
                statusCode: response.status,
                code: 'UNKNOWN',
                message: `Request failed with status ${response.status}`,
            });
        }
        const text = await response.text();
        let parsed = undefined;
        if (text.length > 0) {
            try {
                parsed = JSON.parse(text);
            }
            catch {
                parsed = text;
            }
        }
        if (!response.ok) {
            if (isApiErrorEnvelope(parsed)) {
                throw new ApiError({
                    statusCode: response.status,
                    code: parsed.error,
                    message: parsed.message ?? `Request failed with status ${response.status}`,
                    body: parsed,
                });
            }
            throw new ApiError({
                statusCode: response.status,
                code: 'UNKNOWN',
                message: typeof parsed === 'string' && parsed.length > 0
                    ? parsed
                    : `Request failed with status ${response.status}`,
                body: parsed,
            });
        }
        return parsed;
    }
    return {
        request,
        // Events
        listEvents: () => request('GET', '/events'),
        getEvent: (eventId) => request('GET', `/events/${encodeURIComponent(eventId)}`),
        // Leaderboard (authenticated)
        getLeaderboard: (eventId) => request('GET', `/events/${encodeURIComponent(eventId)}/leaderboard`),
        // Leaderboard (public)
        getPublicLeaderboard: (eventId) => request('GET', `/public/leaderboard/${encodeURIComponent(eventId)}`, {
            public: true,
        }),
        // Scores
        recordScore: (eventId, input) => request('POST', `/events/${encodeURIComponent(eventId)}/scores`, {
            body: input,
        }),
        updateScore: (eventId, scoreId, input) => request('PUT', `/events/${encodeURIComponent(eventId)}/scores/${encodeURIComponent(scoreId)}`, { body: input }),
        syncScoreBatch: (eventId, scores) => request('POST', `/events/${encodeURIComponent(eventId)}/scores/batch`, { body: { scores } }).then((r) => r.results),
        // Participants & teams
        getParticipantScores: (eventId, participantId) => request('GET', `/events/${encodeURIComponent(eventId)}/participants/${encodeURIComponent(participantId)}/scores`),
        // Account & invitations
        listOwnInvitations: () => request('GET', '/account/invitations'),
        acceptInvitation: (invitationId) => request('POST', `/invitations/${encodeURIComponent(invitationId)}/accept`),
        rejectInvitation: (invitationId) => request('POST', `/invitations/${encodeURIComponent(invitationId)}/reject`),
    };
}
//# sourceMappingURL=client.js.map